// ===========================================
// Registration API Functions
// ===========================================
// API calls for the registration flow after M&G approval

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export interface RegistrationValidationData {
  meetAndGreetId: string;
  schoolId: string;
  schoolName: string;
  studentName: string;
  studentAge: number;
  parentName: string;
  parentEmail: string;
  instrumentName: string | null;
  registrationFee: number;
  /** #430: the family already has an account; show no pay button. */
  existingFamily: boolean;
  /** Only meaningful with existingFamily: a registration payment was already taken. */
  paymentReceived?: boolean;
}

export interface CheckoutSessionData {
  sessionId: string;
  url: string;
}

export interface PaymentConfig {
  publishableKey: string;
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// PUBLIC API (No Auth)
// ===========================================

export const registrationApi = {
  /**
   * Validate registration token and get M&G info
   */
  validate: (token: string, mag: string) =>
    apiClient
      .get<ApiResponse<RegistrationValidationData>>(
        `/public/registration/validate?token=${encodeURIComponent(token)}&mag=${encodeURIComponent(mag)}`
      )
      .then((res) => res.data),

  /**
   * Get Stripe publishable key (optionally per-school)
   */
  getPaymentConfig: (schoolId?: string) =>
    apiClient
      .get<ApiResponse<PaymentConfig>>('/payments/config', {
        params: schoolId ? { schoolId } : undefined,
      })
      .then((res) => res.data),

  /**
   * Create checkout session for registration payment
   */
  createCheckoutSession: (params: {
    meetAndGreetId: string;
    schoolId: string;
    successUrl: string;
    cancelUrl: string;
  }) =>
    apiClient
      .post<ApiResponse<CheckoutSessionData>>('/payments/checkout/registration', params)
      .then((res) => res.data),

  /**
   * Verify checkout session after payment
   */
  verifyCheckoutSession: (sessionId: string) =>
    apiClient
      .get<ApiResponse<{ meetAndGreetId: string; paymentIntentId: string; amount: number }>>(
        `/payments/checkout/verify/${sessionId}`
      )
      .then((res) => res.data),

  /**
   * Complete registration after payment
   */
  completeRegistration: (sessionId: string) =>
    apiClient
      .post<ApiResponse<{ familyId: string; studentId: string }>>(
        '/registration/complete',
        { sessionId }
      )
      .then((res) => res.data),
};
